// Utility functions for parsing column order keys

import {
  ColumnOperandIdentifier,
  ColumnValueSegment,
  EExpressionDisplayType,
  IAccountSummary,
  IGroupExpression
} from '../base';
import { AccountSummaryChartValueType, IAccountSummaryChart } from '../accountSummaryCharts';
import { EMPTY_VALUES } from '../../constants';
import { findStaleColumnGroupIds, getColumnGroupIdFromColumnId, resolveColumnSortKeys } from './columnSortKeys';

/**
 * Parses a column order key into its components: columnGroupId and columnName.
 * @param key The column order key string (e.g., 'groupId.columnName.')
 * @returns An object with columnGroupId and columnName
 */
export function parseColumnOrderKey(key: string): { columnGroupId: string; columnName: string } {
  // Handles keys like 'groupId.' or 'groupId.columnName.'
  const [columnGroupId, columnName] = key.endsWith('.') ? key.slice(0, -1).split('.') : key.split('.');
  return {
    columnGroupId: columnGroupId || '',
    columnName: columnName || 'columnName'
  };
}

/**
 * Builds a mapping from column order keys to their order positions.
 * @param columnOrder The columnOrder object from accountSummary
 * @returns A map of column identifiers to their order positions
 */
export function buildColumnOrderMap(columnOrder: Record<string, number>): Map<string, number> {
  // Sort keys by their order value ascending, then build a Map in that order
  const sortedEntries = Object.entries(columnOrder).sort((a, b) => a[1] - b[1]);
  return new Map(sortedEntries);
}

/**
 * A column group's value segment can change between saves — a column with no data in the current
 * slice is keyed as `${id}.Empty Values.` — leaving a saved key that no longer matches any live
 * column value. The summary table reuses that stale key's position (`getSortKeyForColumnGroups`),
 * so reuse it here too, otherwise the chart drops the column to the end while the table keeps it in
 * place. Only stale keys are reused: a saved key that still matches a live column value belongs to
 * that column, so values genuinely missing from the saved order still sort last.
 */
function findStaleOrderForColumnGroup(
  savedOrder: ReadonlyArray<[string, number]>,
  liveKeys: ReadonlySet<string>,
  groupId: string
): number | undefined {
  for (const [key, order] of savedOrder) {
    if (parseColumnOrderKey(key).columnGroupId === groupId && !liveKeys.has(key)) {
      return order;
    }
  }
  return undefined;
}

/**
 * The summary table keys a column with no value segment (per-group Totals, Empty Values) as
 * `${groupId}.Empty Values.` and saves its position under that key, so the chart must build the same key
 * for a null value — otherwise the saved position is never found and the group looks stale.
 */
function buildColumnValueKey(groupId: ColumnValueSegment, value: ColumnValueSegment): string {
  return `${groupId}.${value ?? EMPTY_VALUES}.`;
}

/**
 * Adds an order for every live column value that the saved order does not cover. A stale group (see
 * `findStaleColumnGroupIds`) follows the live column values order, anchored at the group's lowest saved
 * position — the same layout the summary table applies — so chart and table agree. A single live value
 * with a stale key is not a stale group; it keeps reusing the stale key's position (BUD-17797).
 */
function addLiveColumnValueOrders(
  orderMap: Map<string, number>,
  columnOrder: Record<string, number>,
  columnValues: ReadonlyArray<ColumnValueSegment[]>
): void {
  const orderMapSize = orderMap.size;
  const savedOrder = Array.from(orderMap.entries());
  const liveKeys = columnValues.map(([groupId, value]) => buildColumnValueKey(groupId, value));
  const liveKeySet = new Set(liveKeys);
  const staleGroupIds = findStaleColumnGroupIds(columnOrder, liveKeys);
  const staleGroupSortKeys = resolveColumnSortKeys(columnOrder, liveKeys);

  liveKeys.forEach((key, index) => {
    const groupId = getColumnGroupIdFromColumnId(key);
    const fallbackOrder = index + orderMapSize;
    if (staleGroupIds.has(groupId)) {
      orderMap.set(key, staleGroupSortKeys.get(key) ?? fallbackOrder);
      return;
    }
    // Because column values are dynamic, we should anticipate a column order map
    // that does not include all column values for a column group. If a value *does*
    // exist, we don't want to override the custom order.
    if (!orderMap.has(key)) {
      orderMap.set(key, findStaleOrderForColumnGroup(savedOrder, liveKeySet, groupId) ?? fallbackOrder);
    }
  });
}

/**
 * Creates an order map and returns a function to get the order index for a series.
 * This is the core ordering logic shared by chart series and column values ordering.
 * @param columnOrder The columnOrder object
 * @param columnValues Optional column values to add to the order map
 * @returns A function that takes a series and returns its order index
 */
function createOrderIndexGetter<T extends string[] | ColumnValueSegment[]>(
  columnOrder: Record<string, number>,
  columnValues?: T[]
): (series: T) => number {
  const orderMap = buildColumnOrderMap(columnOrder);

  // Add entries for each column value in the column values array
  if (columnValues) {
    addLiveColumnValueOrders(orderMap, columnOrder, columnValues);
  }

  return function getOrderIndex(series: T): number {
    // A null value segment is saved as `Empty Values` (see buildColumnValueKey).
    const seriesValue = series[1] ?? EMPTY_VALUES;
    // Try to find a key in the order map that matches this series
    for (const [key, order] of orderMap.entries()) {
      const { columnGroupId, columnName } = parseColumnOrderKey(key);
      if (series.length === 1 && columnGroupId === series[0]) {
        return Number.MAX_SAFE_INTEGER;
      }
      if (series[0] === columnGroupId && (seriesValue === columnName || columnName === 'columnName')) {
        return order;
      }
    }
    // If not found, order it by the column values
    return Number.MAX_SAFE_INTEGER;
  };
}

/**
 * Updates the chart's column order to match the custom column order in accountSummary.
 * @param chart IAccountSummaryChart object
 * @param accountSummary The current IAccountSummary
 * @param columnValues The current column values
 * @returns A new IAccountSummaryChart with columns reordered
 */
export function orderChartYAxisSeries(
  chart: IAccountSummaryChart,
  accountSummary: IAccountSummary,
  columnValues?: string[][]
): IAccountSummaryChart {
  const columnOrder = accountSummary.columnOrder || {};
  const getOrderIndex = createOrderIndexGetter(columnOrder, columnValues);

  const newYAxis = {
    ...chart.yAxis,
    series: (chart.yAxis?.series || []).slice().sort((a, b) => getOrderIndex(a) - getOrderIndex(b))
  };

  return {
    ...chart,
    yAxis: newYAxis
  };
}

export function orderYAxisSeries(
  series: string[][],
  accountSummary: IAccountSummary,
  columnValues?: string[][]
): string[][] {
  const columnOrder = accountSummary.columnOrder || {};
  const getOrderIndex = createOrderIndexGetter(columnOrder, columnValues);

  return series.slice().sort((a, b) => getOrderIndex(a) - getOrderIndex(b));
}

export function consolidateYAxisSeries(chart: IAccountSummaryChart): IAccountSummaryChart {
  const result = chart.yAxis.series.reduce(
    (acc, item) => {
      const itemId = item[0];
      if (acc.wildcards[itemId]) {
        return acc;
      }
      if (itemId === '*' || item.length === 1) {
        acc.others[itemId] = [];
        acc.wildcards[itemId] = [item];
      } else {
        if (!acc.others[itemId]) {
          acc.others[itemId] = [];
        }
        acc.others[itemId].push(item);
      }
      return acc;
    },
    { wildcards: {}, others: {} }
  );

  const series = result.wildcards['*']
    ? [['*']]
    : Object.values(result.wildcards).concat(Object.values(result.others)).flat();

  return {
    ...chart,
    yAxis: {
      ...chart.yAxis,
      series
    }
  } as IAccountSummaryChart;
}

export function orderColumnValues(columnOrder: IAccountSummary['columnOrder'], columnValues: ColumnValueSegment[][]) {
  const getOrderIndex = createOrderIndexGetter(columnOrder || {}, columnValues);
  return columnValues.slice().sort((a, b) => getOrderIndex(a) - getOrderIndex(b));
}

export function canApplyMultipleYAxis(chart: IAccountSummaryChart): boolean {
  return !!chart.yAxis.multipleAxis?.enabled && !!chart.yAxis.multipleAxis?.series?.length;
}

export function getYAxisSource(chart: IAccountSummaryChart): IAccountSummaryChart['xAxis']['source'] {
  return chart.xAxis.source === 'cols' ? 'rows' : 'cols';
}

export function getValueDisplayTypeforCalculatedColumnGroup(
  columnGroup: IGroupExpression<ColumnOperandIdentifier>
): AccountSummaryChartValueType {
  switch (columnGroup.display) {
    case EExpressionDisplayType.Number:
      return 'numeric';
    case EExpressionDisplayType.Percent:
      return 'percentage';
    case EExpressionDisplayType.Currency:
    default:
      return 'currency';
  }
}

export const generateIdForSeriesItem = (seriesItem: string[]) => {
  return seriesItem.filter(Boolean).join('.');
};

/** Separator between a COA code and its description in a rendered row label ("1015 - Promotion"). */
const COA_LABEL_SEPARATOR = ' - ';

/**
 * Reduces a series-color key to the part of it that survives a rename.
 *
 * A non-MH row-group category id ends in the rendered COA label (`43ba5342….1015 - Community
 * Promotion`), so editing the description changes the id and orphans the color saved against it.
 * Only the code prefix is stable, so both a saved key and a live category id are compared through
 * this form. Keys with nothing to strip — MH ids, column-value ids, plain values — come back
 * unchanged, which is how `resolveSeriesColorIndex` tells "no fallback available" apart from
 * "fallback did not match".
 */
export const normalizeSeriesColorKey = (key: string): string => {
  // Scanning right to left keeps the innermost label the one that gets stripped, so a nested id
  // ("rg1.100 - Funds.1015 - Promotion") keeps its parent intact. The description may itself contain
  // both dots ("1015 - Dept. of Parks") and the separator ("1015 - Parks - Recreation"), so the code
  // is bounded by the last dot before the separator rather than by splitting the key into segments.
  let separatorIndex = key.lastIndexOf(COA_LABEL_SEPARATOR);

  while (separatorIndex > 0) {
    const code = key.slice(key.lastIndexOf('.', separatorIndex) + 1, separatorIndex);

    // A value can contain ' - ' without being a COA label, and treating one as a code would let a
    // saved color leak onto a genuinely different category — "Q1 - Actuals" and "Q1 - Budget" are
    // two categories, not one renamed twice. Every COA code in the data is a plain integer, so that
    // is what separates the two; anything else just means no fallback is available for this key.
    if (/^\d+$/.test(code)) {
      return key.slice(0, separatorIndex);
    }

    separatorIndex = key.lastIndexOf(COA_LABEL_SEPARATOR, separatorIndex - 1);
  }

  return key;
};

/**
 * Position of the category a saved series color belongs to, or -1 when it cannot be placed.
 *
 * An exact id match always wins. Otherwise the key is matched by its rename-stable form, but only
 * when exactly one category answers to it: two categories sharing a code cannot be told apart, and
 * repainting the wrong series is worse than falling back to the palette color.
 */
export const resolveSeriesColorIndex = (savedKey: string, categoryIds: string[]): number => {
  const exactIndex = categoryIds.indexOf(savedKey);
  if (exactIndex !== -1) {
    return exactIndex;
  }

  const normalizedSavedKey = normalizeSeriesColorKey(savedKey);
  if (normalizedSavedKey === savedKey) {
    return -1;
  }

  const matchedIndices = categoryIds.reduce<number[]>((acc, categoryId, index) => {
    if (normalizeSeriesColorKey(categoryId) === normalizedSavedKey) {
      acc.push(index);
    }
    return acc;
  }, []);

  return matchedIndices.length === 1 ? matchedIndices[0] : -1;
};

/**
 * Pie and donut charts can aggregate the tail of their slices into a synthesized "Other" slice.
 * That slice is not one of the report's categories, so it has no `seriesColors` entry and cannot be
 * recolored. Both the chart pipeline that produces the id and the settings form that reacts to a
 * click on it resolve the id through these helpers.
 */
const OTHER_SLICE_ID = 'other';

export const generateIdForOtherSlice = (firstAggregatedSliceId?: string) => {
  return firstAggregatedSliceId ? `${OTHER_SLICE_ID}-${firstAggregatedSliceId}` : OTHER_SLICE_ID;
};

export const isOtherSliceId = (id?: string) => {
  return id === OTHER_SLICE_ID || Boolean(id?.startsWith(`${OTHER_SLICE_ID}-`));
};

export function hasAllColumnGroupsWildcard(chart: IAccountSummaryChart): boolean {
  return chart.yAxis.series.every((item) => item.length === 1 && item[0] === '*');
}

export function hasDynamicColumnValues(chart: IAccountSummaryChart): boolean {
  return chart.yAxis.series.some((item) => item.length === 1);
}

export function hasAllRowGroupsWildcard(chart: IAccountSummaryChart): boolean {
  return chart.xAxis.series.every((item) => item.length === 1 && item[0] === '*');
}

export function hasDynamicRowValues(chart: IAccountSummaryChart): boolean {
  return chart.xAxis.series.some((item) => item.length === 2 && item[1] === '*');
}
